'use client';

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { StepUpDialog } from './StepUpDialog';

interface StepUpResult {
  /** HTTP-level ok (res.ok) of the final response. */
  ok: boolean;
  status: number;
  /** Parsed JSON body (best-effort; `{}` if non-JSON). */
  data: unknown;
  /** True when the user dismissed the step-up dialog → action not performed. */
  aborted?: boolean;
}

interface StepUpContextValue {
  /** Opens the re-auth dialog; resolves `true` when verified, `false` if dismissed. */
  requestStepUp: () => Promise<boolean>;
  /**
   * `fetch` that transparently satisfies a step-up gate: on a `401` carrying
   * `{ code: 'STEP_UP_REQUIRED' }` it opens the re-auth dialog and, once the
   * user re-verifies, retries the request exactly once. Use for ANY call to a
   * `requires_step_up` endpoint (billing.manage, audit.export, …).
   */
  fetchWithStepUp: (url: string, init?: RequestInit) => Promise<StepUpResult>;
}

const StepUpContext = createContext<StepUpContextValue | null>(null);

/**
 * App-wide step-up re-authentication. Mounts a single {@link StepUpDialog} and
 * exposes {@link useStepUp}. Sensitive permissions are step-up-gated server-side
 * (RBAC `requires_step_up`); without a client flow those calls fail silently.
 * One provider in the admin layout gives every sensitive action the re-auth +
 * retry behaviour for free — no per-component modal duplication.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<((verified: boolean) => void) | null>(null);

  const settle = useCallback((verified: boolean) => {
    resolverRef.current?.(verified);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const requestStepUp = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setOpen(true);
      }),
    [],
  );

  const fetchWithStepUp = useCallback(
    async (url: string, init?: RequestInit): Promise<StepUpResult> => {
      const run = () => fetch(url, init);
      let res = await run();
      let data: unknown = await res.json().catch(() => ({}));
      if (
        res.status === 401 &&
        typeof data === 'object' &&
        data !== null &&
        (data as { code?: string }).code === 'STEP_UP_REQUIRED'
      ) {
        const verified = await requestStepUp();
        if (!verified) return { ok: false, status: res.status, data, aborted: true };
        res = await run();
        data = await res.json().catch(() => ({}));
      }
      return { ok: res.ok, status: res.status, data };
    },
    [requestStepUp],
  );

  return (
    <StepUpContext.Provider value={{ requestStepUp, fetchWithStepUp }}>
      {children}
      <StepUpDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) settle(false);
          else setOpen(true);
        }}
        onVerified={() => settle(true)}
      />
    </StepUpContext.Provider>
  );
}

export function useStepUp(): StepUpContextValue {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error('useStepUp must be used within a StepUpProvider');
  return ctx;
}
